import { EventSchema } from "../data/validationSchemas/EventSchema";

export const SPREADSHEET_COLUMNS = [
  { key: "eventName", label: "Event name" },
  { key: "description", label: "Description" },
  { key: "startDate", label: "Start date" },
  { key: "startTime", label: "Start time" },
  { key: "endTime", label: "End time" },
  { key: "location", label: "Location" },
];

export const MIN_TRAILING_BLANK_ROWS = 3;

const columnIndexByField = SPREADSHEET_COLUMNS.reduce((indexes, column, index) => {
  indexes[column.key] = index;
  return indexes;
}, {});

export const getSpreadsheetCellValue = (cell) => {
  if (cell?.value === null || cell?.value === undefined) return "";
  return String(cell.value);
};

export const isSpreadsheetRowBlank = (row = []) =>
  SPREADSHEET_COLUMNS.every((_, columnIndex) =>
    getSpreadsheetCellValue(row[columnIndex]).trim() === "",
  );

const createBlankRow = () => SPREADSHEET_COLUMNS.map(() => ({ value: "" }));

const normalizeCell = (cell) => ({
  ...(cell || {}),
  value: getSpreadsheetCellValue(cell),
});

export function normalizeSpreadsheetGrid(
  grid = [],
  minimumTrailingRows = MIN_TRAILING_BLANK_ROWS,
) {
  const normalized = grid.map((row = []) =>
    SPREADSHEET_COLUMNS.map((_, columnIndex) => normalizeCell(row[columnIndex])),
  );

  let blankRowsAtEnd = 0;
  for (let index = normalized.length - 1; index >= 0; index -= 1) {
    if (!isSpreadsheetRowBlank(normalized[index])) break;
    blankRowsAtEnd += 1;
  }

  while (blankRowsAtEnd < minimumTrailingRows) {
    normalized.push(createBlankRow());
    blankRowsAtEnd += 1;
  }

  return normalized;
}

export function buildSpreadsheetGrid(rows = []) {
  const body = rows.map((row) =>
    SPREADSHEET_COLUMNS.map(({ key }) => ({ value: row?.[key] ?? "" })),
  );

  return normalizeSpreadsheetGrid(body);
}

const normalizeEventParticipants = (participants) => {
  if (Array.isArray(participants)) return participants;
  if (participants && typeof participants === "object") {
    return Object.values(participants);
  }
  return undefined;
};

export function spreadsheetRowToEvent(
  row,
  { originalEvent, rowId, gatheringID } = {},
) {
  const event = { ...(originalEvent || {}) };

  SPREADSHEET_COLUMNS.forEach(({ key }, columnIndex) => {
    event[key] = getSpreadsheetCellValue(row?.[columnIndex]);
  });

  const id = rowId || originalEvent?.id || originalEvent?._id;
  if (id) event.id = id;
  else delete event.id;

  event.gatheringID = originalEvent?.gatheringID || gatheringID || "";

  const participants = normalizeEventParticipants(event.eventParticipants);
  if (participants) event.eventParticipants = participants;
  else delete event.eventParticipants;

  return event;
}

const getRowOptions = (rowIndex, options) => ({
  originalEvent: options.originalRows?.[rowIndex],
  rowId: options.rowIds?.[rowIndex],
  gatheringID: options.gatheringID,
});

export function validateSpreadsheetGrid(grid = [], options = {}) {
  const errors = {};

  grid.forEach((row, rowIndex) => {
    const rowOptions = getRowOptions(rowIndex, options);
    if (isSpreadsheetRowBlank(row) && !rowOptions.rowId) return;

    const result = EventSchema.safeParse(spreadsheetRowToEvent(row, rowOptions));
    if (result.success) return;

    result.error.issues.forEach((issue) => {
      const field = issue.path[0];
      const columnIndex = columnIndexByField[field];
      if (columnIndex === undefined) return;

      const errorKey = `${rowIndex}-${columnIndex}`;
      if (!errors[errorKey]) errors[errorKey] = issue.message;
    });
  });

  return errors;
}

export function annotateSpreadsheetGrid(grid = [], errors = {}) {
  return grid.map((row, rowIndex) =>
    row.map((cell, columnIndex) => {
      const error = errors[`${rowIndex}-${columnIndex}`];
      const classNames = String(cell?.className || "")
        .split(/\s+/)
        .filter(Boolean)
        .filter((className) => className !== "ss-invalid-cell");

      if (error) classNames.push("ss-invalid-cell");

      return {
        ...(cell || {}),
        value: getSpreadsheetCellValue(cell),
        className: classNames.join(" "),
        error,
      };
    }),
  );
}

const hasEditableChange = (event, originalEvent) => {
  if (!originalEvent) return true;
  return SPREADSHEET_COLUMNS.some(
    ({ key }) => String(event[key] ?? "") !== String(originalEvent[key] ?? ""),
  );
};

export function spreadsheetGridToChanges(grid = [], options = {}) {
  const changes = [];

  grid.forEach((row, rowIndex) => {
    const rowOptions = getRowOptions(rowIndex, options);
    if (isSpreadsheetRowBlank(row) && !rowOptions.rowId) return;

    const result = EventSchema.safeParse(spreadsheetRowToEvent(row, rowOptions));
    if (!result.success) {
      throw result.error;
    }

    const event = {
      ...(rowOptions.originalEvent || {}),
      ...result.data,
    };

    if (hasEditableChange(event, rowOptions.originalEvent)) {
      changes.push({ rowIndex, event });
    }
  });

  return changes;
}
